<template>
  <modal
    class="custom-report-editor"
    :append-to-body="true"
    v-model="shown"
    :title="
      'Editing - ' + ((groupClone.name || '').length > 0 ? groupClone.name : 'Untitled Report')
    "
    data-test="custom-report-editor"
  >
    <div style="width: 100%">
      <input
        type="text"
        class="form-control input-lg"
        v-model="groupClone.name"
        style="width: 100%; max-width: 100%"
        placeholder="Give this report a title"
        data-test="custom-report-editor-name-input"
        aria-label="Give this report a title"
      />

      <div style="height: 10px"></div>

      <vue-tags-input
        style="width: 100%"
        class="form-control input-lg text-left"
        v-model="tag"
        :placeholder="'Enter names...'"
        :maxlength="128"
        :max-tags="200"
        :tags="groupClone.memberTags"
        :validation="tagValidation"
        @tags-changed="(newTags) => (groupClone.memberTags = newTags)"
        :add-on-key="[13, 9, ':', ';']"
        :autocomplete-items="autocompleteItems()"
        :add-only-from-autocomplete="true"
        :autocomplete-min-length="0"
        data-test="custom-report-editor-tags-input"
      />
      <div style="height: 10px"></div>
      <p class="text-danger text-left" v-for="error in errors" v-bind:key="error">{{ error }}</p>
      <div style="height: 10px"></div>
    </div>

    <template #footer>
      <div>
        <button
          class="btn btn-default"
          @click="cancel()"
          data-test="custom-report-editor-cancel-button"
          type="button"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          @click="ok()"
          data-test="custom-report-editor-ok-button"
          type="button"
        >
          OK
        </button>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import axios from "axios";
import { Modal } from "uiv";
import VueTagsInput from "@sipec/vue3-tags-input";
import { CustomReportApiModel } from "../api-models";
import { validationErrors, CustomReportMemberTag } from "./custom-report-api-model";

const props = defineProps<{
  group: CustomReportApiModel;
  availableMemberTags: CustomReportMemberTag[];
  redirectUrlBase: string;
  patchUrl: string;
}>();

const emit = defineEmits(["dialog-closed", "dialog-opened"]);

const errors = ref<string[]>([]);
const shown = ref(false);
const tag = ref("");
const groupClone = ref<CustomReportApiModel>({} as any);

const tagValidation = [
  {
    classes: "lacks-report",
    rule: (memberTag: CustomReportMemberTag): boolean => !memberTag.hasReport,
  },
];

const autocompleteItems = () => {
  return props.availableMemberTags.filter((i) => {
    return i.text.toLowerCase().includes(tag.value.toLowerCase());
  });
};

const overlay = (from: CustomReportApiModel, on: CustomReportApiModel): void => {
  on.id = from.id;
  on.memberTags = on.memberTags ?? [];
  on.memberTags.length = 0;
  from.memberTags.forEach((x) => on.memberTags.push(x));
  on.name = from.name;
  on.kind = from.kind;
};

const open = (): void => {
  setTimeout(() => {
    shown.value = true;
  }, 200);
  emit("dialog-opened");
};

const close = (): void => {
  shown.value = false;
  setTimeout(() => {
    emit("dialog-closed");
  }, 1000);
};

const cancel = (): void => {
  close();
};

const validate = (): void => {
  const localErrors = validationErrors(groupClone.value);
  errors.value = localErrors;
};

const ok = (): void => {
  validate();
  if (errors.value.length === 0) {
    const href = `${props.redirectUrlBase}/${groupClone.value.id || ""}`.replace("//", "/");
    const newTab = window.open("", "_blank");

    axios
      .patch(props.patchUrl, groupClone.value)
      .then(() => {
        overlay(groupClone.value, props.group);
        if (errors.value.length === 0) {
          if (newTab) {
            newTab.location.href = href;
          } else {
            window.location.href = href;
          }
          close();
        }
      })
      .catch(() => {
        errors.value.push(
          "There was a problem. Please check your internet connection and try again.",
        );
        if (newTab !== null) {
          newTab.close();
        }
      });
  }
};

onMounted(() => {
  overlay(props.group, groupClone.value);
});

const getGroup = (): CustomReportApiModel => {
  return groupClone.value;
};

defineExpose({
  open,
  close,
  cancel,
  validate,
  ok,
  getGroup,
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../styles/bootstrap/bootstrap.less";

.custom-report-editor {
  .ti-autocomplete {
    position: static;
    margin-left: -1px;
    margin-right: -1px;
    margin-bottom: -4px;
    border-bottom-right-radius: 2px;
    border-bottom-left-radius: 2px;
    width: auto;
  }

  .ti-autocomplete .lacks-report.lacks-report {
    :after {
      content: " - Waiting for report...";
      color: #999;
      display: inline;
    }
  }

  .ti-autocomplete .ti-selected-item.lacks-report.lacks-report {
    :after {
      content: " - Waiting for report...";
      color: #444;
      display: inline;
    }
  }

  .ti-tag.lacks-report.lacks-report.lacks-report.lacks-report {
    background-color: #ddd;
    color: #777;
    .ti-icon-close {
      color: black;
    }
  }
}
</style>
